import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { lineReveal } from "@/lib/motion";
import { scrollToId } from "@/lib/scroll";
import { IMAGES } from "@/data/content";

const LINES = [
  "Uma Igreja enraizada",
  "na fé apostólica, aberta",
  "à missão e presente",
  "no povo brasileiro.",
];

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 180]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.3]);

  return (
    <section id="inicio" className="relative min-h-[92vh] flex items-end overflow-hidden bg-[#2E070D]">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-24">
        <img src={IMAGES.hero} alt="Vitral de catedral" fetchPriority="high" className="h-full w-full object-cover scale-110" />
      </motion.div>
      <div className="absolute inset-0 bg-[#2E070D]/72" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#2E070D]/85 via-[#2E070D]/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#2E070D] to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-4 sm:px-8 lg:px-16 pb-24 pt-48 text-[#FBF9F4]">
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="overline-label text-[#C5A059] mb-8"
          data-testid="hero-overline"
        >
          Diocese Anglicana Católica do Brasil
        </motion.h1>

        <p data-testid="hero-title-heading" className="font-display font-bold uppercase leading-[1.05] tracking-tight text-4xl sm:text-6xl lg:text-7xl max-w-5xl">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span custom={i} variants={lineReveal} initial="hidden" animate="show" className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-col lg:flex-row lg:items-end gap-10"
        >
          <blockquote data-testid="hero-verse-quote" className="max-w-md border-l-2 border-[#C5A059] pl-5">
            <p className="font-liturgy italic text-xl sm:text-2xl text-[#FBF9F4]/90">«Ide por todo o mundo e pregai o Evangelho»</p>
            <cite className="overline-label text-[#C5A059] not-italic">Mc 16,15</cite>
          </blockquote>

          <div className="flex flex-wrap gap-4 lg:ml-auto">
            <button
              data-testid="hero-cta-diocese"
              onClick={() => scrollToId("a-diocese")}
              className="overline-label bg-[#C5A059] text-[#2E070D] px-8 py-4 hover:bg-[#D4AF37] transition-colors"
            >
              Conheça a Diocese
            </button>
            <button
              data-testid="hero-cta-comunidade"
              onClick={() => scrollToId("comunidades")}
              className="overline-label border border-[#FBF9F4]/40 text-[#FBF9F4] px-8 py-4 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
            >
              Encontre uma Comunidade
            </button>
          </div>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("destaques")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#C5A059] animate-pulse-soft"
        aria-label="Rolar para baixo"
      >
        <ChevronDown className="h-6 w-6" />
      </motion.button>
    </section>
  );
}
